import { CONDITION_VALUE_TYPES } from '@saesak/shared';
import { Chip, Input } from './ui';
import type { ConditionTag } from '@/types';

export type ConditionValue = unknown;

/**
 * 컨디션 태그 입력 — 명세서 §5.3 (아이콘 탭 입력)
 *
 * 태그 정의는 서버가 내려주는 데이터라(`ConditionTagDefinition`) 이 컴포넌트는
 * `valueType`에 따라 입력 형태만 갈아 끼운다. 태그가 늘거나 줄어도 코드를 고칠 필요가 없다.
 */
export function ConditionTagInput({
  tag,
  value,
  onChange,
}: {
  tag: ConditionTag;
  value: ConditionValue;
  onChange: (next: ConditionValue) => void;
}) {
  return (
    <div className="py-2">
      <div className="mb-1.5 flex items-baseline gap-2">
        <span className="text-sm font-semibold text-ink">{tag.label}</span>
        {/* LINKED는 본문이 힌트를 그대로 보여 주므로 여기서 또 쓰면 같은 문장이 두 번 나온다 */}
        {tag.hint && tag.valueType !== CONDITION_VALUE_TYPES.LINKED && (
          <span className="text-[12px] text-ink-sub">{tag.hint}</span>
        )}
      </div>
      <Control tag={tag} value={value} onChange={onChange} />
    </div>
  );
}

function Control({
  tag,
  value,
  onChange,
}: {
  tag: ConditionTag;
  value: ConditionValue;
  onChange: (next: ConditionValue) => void;
}) {
  switch (tag.valueType) {
    case CONDITION_VALUE_TYPES.CHOICE:
      return (
        <div className="flex flex-wrap gap-1.5">
          {tag.options?.map((option) => (
            <Chip
              key={option.value}
              tone={value === option.value ? 'primary' : 'neutral'}
              selected={value === option.value}
              onClick={() => onChange(value === option.value ? undefined : option.value)}
            >
              {option.label}
            </Chip>
          ))}
        </div>
      );

    case CONDITION_VALUE_TYPES.MULTI_CHOICE: {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="flex flex-wrap gap-1.5">
          {tag.options?.map((option) => {
            const on = selected.includes(option.value);
            return (
              <Chip
                key={option.value}
                tone={on ? 'primary' : 'neutral'}
                selected={on}
                onClick={() => {
                  const next = on
                    ? selected.filter((v) => v !== option.value)
                    : [...selected, option.value];
                  onChange(next.length > 0 ? next : undefined);
                }}
              >
                {option.label}
              </Chip>
            );
          })}
        </div>
      );
    }

    case CONDITION_VALUE_TYPES.COUNT: {
      const count = typeof value === 'number' ? value : 0;
      return (
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="줄이기"
            className="tap grid place-items-center rounded-full bg-bg text-lg font-bold text-ink"
            onClick={() => onChange(count > 0 ? count - 1 : undefined)}
          >
            −
          </button>
          <span className="tnum min-w-12 text-center text-lg font-semibold">
            {count}
            {tag.unit}
          </span>
          <button
            type="button"
            aria-label="늘리기"
            className="tap grid place-items-center rounded-full bg-primary-soft text-lg font-bold text-primary-strong"
            onClick={() => onChange(count + 1)}
          >
            +
          </button>
        </div>
      );
    }

    case CONDITION_VALUE_TYPES.NUMBER:
      return (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            inputMode="decimal"
            step="0.1"
            className="w-32"
            value={typeof value === 'number' ? value : ''}
            onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
          />
          <span className="text-base text-ink-sub">{tag.unit}</span>
        </div>
      );

    case CONDITION_VALUE_TYPES.TIME_RANGE: {
      const range = (value ?? {}) as { start?: string; end?: string };
      const update = (patch: Partial<typeof range>) => {
        const next = { ...range, ...patch };
        onChange(next.start || next.end ? next : undefined);
      };
      return (
        <div className="flex items-center gap-2">
          <Input
            type="time"
            className="w-32"
            value={range.start ?? ''}
            onChange={(e) => update({ start: e.target.value })}
          />
          <span className="text-ink-sub">–</span>
          <Input
            type="time"
            className="w-32"
            value={range.end ?? ''}
            onChange={(e) => update({ end: e.target.value })}
          />
        </div>
      );
    }

    case CONDITION_VALUE_TYPES.LINKED:
      return (
        <p className="rounded-sm bg-bg px-3 py-2 text-sm text-ink-sub">
          {tag.hint ?? '다른 화면에서 기록하면 자동으로 채워져요.'}
        </p>
      );

    default:
      return (
        <Input
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || undefined)}
        />
      );
  }
}
